import type { QueueEntry, QueueState } from './queueModel'

/**
 * Resume-after-refresh (D1) — the capability David explicitly asked us to retain from v1, extended
 * on 2026-07-24 to cover the WHOLE transfer queue rather than only the file in flight.
 *
 * ═══ THE BROWSER FACT THIS IS BUILT AROUND ═══
 * After a full page reload a `File` object is gone, and NO web app can read the chosen file's
 * bytes again unless the user re-picks it or we hold a File System Access handle (Chromium only —
 * not Safari, not Firefox). It is a security invariant, not a v1 shortcoming.
 *
 * ⇒ So the baseline is: remember WHAT was in the queue and WHERE the server got to, then ask the
 * provider to re-select those files and continue. That is exactly what v1 did (it matched on name +
 * size), it works in EVERY browser, and — because Mac Safari is a first-class target (§0e) — it is
 * the PRIMARY path here, not a fallback.
 *
 * ★ What makes the resume real rather than cosmetic is the SERVER: the offset is the byte length
 * on disk, so once the same file is re-selected, tus asks "how much do you have?" and continues
 * from there. Nothing about the progress is trusted from this store — it is only a pointer.
 *
 * ★★ TWO KINDS OF RECORD LIVE HERE, and the difference is simply whether `url` is set:
 *   · WITH a url — bytes are already on the server. This is the "Unfinished upload" banner, and
 *     re-picking continues from the server's offset.
 *   · WITHOUT one — the file was sitting in the queue and had never started. Nothing exists
 *     server-side; re-picking just puts the file back where it was.
 * Both need the same thing from the provider (the file again), which is why one store holds both
 * and one re-pick can satisfy them together.
 */

const KEY = 'yoreflix.upload.resume'

/** States worth remembering. `complete` is finished business; `cancelled` was thrown away. */
const LIVE_STATES: readonly QueueState[] = ['ready', 'queued', 'uploading', 'failed']

export interface ResumeRecord {
  id: string
  name: string
  size: number
  projectKey: string
  /**
   * What the entry was doing when the page went away.
   *
   * ★ Restored faithfully, because it carries the provider's own decision: a `queued` entry means
   * they had already pressed Start, so it should carry on the moment its file is back. A `ready`
   * one was still waiting for them and must keep waiting — silently starting a transfer they never
   * armed is exactly the mistake explicit Start exists to prevent.
   */
  state: QueueState
  /** Last known progress. DISPLAY ONLY — the server's offset is the truth when it resumes. */
  sent: number
  /** The tus upload URL. Its presence is what says "bytes exist on the server". */
  url?: string
}

export function loadResumable(): ResumeRecord[] {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isRecord)
  } catch {
    // ⚠ Safari Private Browsing restricts persistent storage, so this can throw. A portal that
    // cannot remember an interrupted upload is degraded; one that white-screens is broken.
    return []
  }
}

/**
 * Persist the queue AND the records not currently in it.
 *
 * ⚠⚠ THE SECOND ARGUMENT IS NOT OPTIONAL POLISH — IT FIXES A REAL DATA-LOSS BUG. This used to
 * persist the queue alone, and the screen restores banner records into `resume` (not into `queue`)
 * on mount. So the save effect fired on mount with an empty queue and REMOVED the whole key: the
 * interrupted upload survived exactly one reload, because the record was already in memory. Reload
 * twice without touching it and the pointer to those server-side bytes was gone for good — while
 * the bytes themselves sat on the box with nothing left referring to them.
 *
 * ⇒ What is written is the UNION of both, deduplicated by id. Anything the screen still knows
 * about is still remembered.
 */
export function saveResumable(entries: readonly QueueEntry[], keep: readonly ResumeRecord[] = []): void {
  const records: ResumeRecord[] = entries
    .filter((e) => LIVE_STATES.includes(e.state))
    .map((e) => ({ id: e.id, name: e.name, size: e.size, projectKey: e.projectKey, state: e.state, sent: e.sent, url: e.url }))

  const seen = new Set(records.map((r) => r.id))
  for (const record of keep) {
    if (!seen.has(record.id)) records.push(record)
  }

  try {
    if (records.length === 0) localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, JSON.stringify(records))
  } catch {
    // See above — best-effort by design.
  }
}

export function clearResumable(id: string): void {
  const kept = loadResumable().filter((r) => r.id !== id)
  saveResumable([], kept)
}

/**
 * Turn a stored record back into a queue entry — WITHOUT its bytes.
 *
 * ⚠ `file` is deliberately absent, and that is what makes the restored row safe: `nextToStart`
 * refuses to start an entry with no file, so a restored queue sits still until the provider hands
 * the files back. The row is a placeholder holding its place in the order, not a live transfer.
 */
export function restoredEntry(record: ResumeRecord): QueueEntry {
  // ⚠ Normalised, not trusted: this comes back from localStorage, where a record may predate the
  // field or have been edited by hand. An unrecognised state must not produce an entry the queue
  // model has no rule for.
  const stored = LIVE_STATES.includes(record.state) ? record.state : 'ready'

  return {
    id: record.id,
    name: record.name,
    size: record.size,
    projectKey: record.projectKey,
    // ⚠ An entry that was mid-flight but has NO server url never really started (creation failed).
    // Putting it back as `ready` makes the provider arm it again rather than resuming a fiction.
    state: stored === 'uploading' ? 'ready' : stored,
    sent: Number.isFinite(record.sent) ? record.sent : 0,
    url: record.url,
  }
}

/**
 * Does this re-selected file match the interrupted one?
 *
 * ★ Name AND size, both — v1's rule, and it is the right one. Name alone would happily resume a
 * DIFFERENT cut of the same film onto the half-uploaded bytes of the old one, producing a file
 * that is corrupt in a way nothing downstream would notice.
 *
 * ⚠ It is a guard against honest mistakes, not an integrity proof. Two different files can share
 * a name and a byte count; the real protection is that the provider is re-picking their own file
 * moments after their own upload dropped.
 */
export function fileMatches(record: Pick<ResumeRecord, 'name' | 'size'>, file: File): boolean {
  return file.name === record.name && file.size === record.size
}

/** Why a re-selected file was refused — worth saying precisely, since the fix differs. */
export function matchError(record: ResumeRecord, file: File): string | null {
  if (file.name !== record.name) return `That is a different file — expected “${record.name}”.`
  if (file.size !== record.size) return 'That file is a different size to the one that was interrupted.'
  return null
}

function isRecord(value: unknown): value is ResumeRecord {
  if (typeof value !== 'object' || value === null) return false
  const r = value as Record<string, unknown>
  return (
    typeof r.id === 'string' &&
    typeof r.name === 'string' &&
    typeof r.size === 'number' &&
    typeof r.projectKey === 'string' &&
    // ⚠ `state`/`sent`/`url` are all tolerated as missing: records written before the queue was
    // persisted carry only the first four fields, and a provider mid-upload across a deploy must
    // not silently lose their resume pointer because the shape grew.
    (r.url === undefined || typeof r.url === 'string')
  )
}
